import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import { alpha } from "@mui/material/styles";

import { useAppDispatch, useAppSelector } from "app/hooks";
import {
  logoutUser,
  selectCurrentUser,
  selectCurrentUserToken
} from "features/auth/auth-slice";

import PracticeSelector from "../layout-top-bar/practice-selector/practice-selector.component";
import useSwitchPractice from "../layout-top-bar/practice-selector/use-switch-practice.hook";
import useActiveSessionContext from "./use-active-session-context.hook";

/**
 * Persistent context header for multi-practice users. Hosts the practice
 * switcher so the regular nav bar stays uncluttered. Two personas:
 *  - Internal Suno staff / break-glass → RED. Labelled "Admin" at the home
 *    practice, "Admin Mode" once switched into a foreign practice.
 *  - Franchise corporate (grant) users → NEUTRAL "Viewing".
 *
 * The header is always shown for multi-practice users (so they can start a
 * switch from home). Exit (only while switched) returns to the home practice;
 * a break-glass session — which can't switch — is ended instead.
 */
export default function PracticeContextBanner() {
  const dispatch = useAppDispatch();
  const token = useAppSelector(selectCurrentUserToken);
  const currentUser = useAppSelector(selectCurrentUser);
  const {
    isSwitched,
    isInternal,
    isBreakGlass,
    activePracticeId,
    homePracticeId
  } = useActiveSessionContext();
  const { switchToPractice, isLoading } = useSwitchPractice();

  // Only multi-practice users get the header (and the switcher inside it).
  if (!currentUser?.is_multi_practice_user) return null;

  const handleExit = () => {
    if (isBreakGlass) {
      // Break-glass sessions cannot switch back (the backend rejects it); end
      // the session instead.
      if (token) dispatch(logoutUser({ token }));
      return;
    }
    if (homePracticeId != null) {
      switchToPractice(homePracticeId, activePracticeId ?? undefined);
    }
  };

  const prefix = isSwitched
    ? isInternal
      ? "Admin Mode"
      : "Viewing"
    : isInternal
      ? "Admin"
      : null;

  return (
    <Box
      // Internal/admin context is safety-critical — announce it assertively;
      // the neutral franchise context is polite.
      role={isInternal ? "alert" : "status"}
      data-testid="practice-context-banner"
      data-admin-mode={isInternal}
      data-switched={isSwitched}
      sx={{
        position: "relative",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 0.75,
        px: 2,
        py: 0.5,
        minHeight: 36,
        bgcolor: isInternal
          ? "error.main"
          : (theme) => alpha(theme.palette.info.main, 0.12),
        color: isInternal ? "error.contrastText" : "text.primary",
        borderBottom: isInternal
          ? "none"
          : (theme) => `1px solid ${alpha(theme.palette.info.main, 0.3)}`
      }}
    >
      {prefix && (
        <Typography
          variant="body2"
          fontWeight={700}
          sx={{ whiteSpace: "nowrap" }}
        >
          {prefix} ·
        </Typography>
      )}
      <PracticeSelector />
      {isSwitched && (
        <Button
          size="small"
          variant={isInternal ? "outlined" : "text"}
          onClick={handleExit}
          disabled={isLoading}
          data-testid="practice-context-exit"
          aria-label={isInternal ? "Exit admin mode" : "Exit practice view"}
          sx={{
            position: "absolute",
            right: 12,
            minHeight: 28,
            px: 1.25,
            // White on the red admin header; default text colour on neutral.
            color: isInternal ? "common.white" : "text.primary",
            borderColor: isInternal
              ? (theme) => alpha(theme.palette.common.white, 0.7)
              : undefined,
            "&:hover": isInternal
              ? {
                  borderColor: "common.white",
                  backgroundColor: (theme) =>
                    alpha(theme.palette.common.white, 0.12)
                }
              : undefined
          }}
        >
          Exit
        </Button>
      )}
    </Box>
  );
}
